import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import {
  HomeIcon,
  UserGroupIcon,
  ArrowLeftIcon,
  ChartBarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  UsersIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";

// Color palette for the pie chart slices
const PIE_COLORS = [
  "#3b82f6", // blue
  "#10b981", // green
  "#f59e0b", // amber
  "#8b5cf6", // purple
  "#ef4444", // red
  "#06b6d4", // cyan
  "#ec4899", // pink
  "#84cc16", // lime
  "#f97316", // orange
  "#6366f1", // indigo
];

// Lightweight SVG pie chart with rich hover tooltip (no external deps)
const SchemePieChart = ({ data }) => {
  const size = 220;
  const radius = 95;
  const cx = size / 2;
  const cy = size / 2;

  const [hover, setHover] = useState(null); // { index, x, y }

  const total = data.reduce((sum, d) => sum + (d.total_usdt || 0), 0);

  if (!data.length || total <= 0) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-500 text-sm">
        No investment data available for this mobile number.
      </div>
    );
  }

  const formatUSDT = (v) =>
    `$${Number(v || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const handleMove = (e, idx) => {
    const rect = e.currentTarget.ownerSVGElement.getBoundingClientRect();
    setHover({
      index: idx,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const renderTooltip = () => {
    if (!hover) return null;
    const d = data[hover.index];
    if (!d) return null;
    const pct = total > 0 ? (d.total_usdt / total) * 100 : 0;

    // Position tooltip; clamp horizontally so it stays inside the SVG box
    const tooltipWidth = 240;
    const tooltipHeight = 170;
    let left = hover.x + 12;
    if (left + tooltipWidth > size) left = hover.x - tooltipWidth - 12;
    if (left < 0) left = 4;
    let top = hover.y + 12;
    if (top + tooltipHeight > size) top = hover.y - tooltipHeight;
    if (top < 0) top = 4;

    return (
      <div
        className="pointer-events-none absolute z-10 rounded-lg shadow-xl border border-gray-200 bg-white px-3 py-2 text-xs"
        style={{ left, top, width: tooltipWidth }}
      >
        <div className="flex items-center gap-2 mb-1">
          <span
            className="inline-block w-3 h-3 rounded-sm flex-shrink-0"
            style={{
              backgroundColor: PIE_COLORS[hover.index % PIE_COLORS.length],
            }}
          />
          <span className="font-semibold text-gray-900 truncate">
            {d.scheme_name}
            {d.scheme_code ? ` (${d.scheme_code})` : ""}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-gray-700">
          <span className="text-gray-500">Total USDT</span>
          <span className="text-right font-medium text-gray-900">
            {formatUSDT(d.total_usdt)}
          </span>
          <span className="text-gray-500">Share</span>
          <span className="text-right font-medium text-gray-900">
            {pct.toFixed(2)}%
          </span>
          <span className="text-gray-500">Investments</span>
          <span className="text-right font-medium text-gray-900">
            {d.investment_count}
          </span>
        </div>
        <div className="mt-2 pt-2 border-t border-gray-100 grid grid-cols-2 gap-x-2 gap-y-0.5">
          <span className="text-yellow-700 font-medium">With UNS</span>
          <span className="text-right text-gray-900">
            {formatUSDT(d.with_uns_usdt || 0)}
            <span className="text-gray-400 ml-1">
              ({d.with_uns_count || 0})
            </span>
          </span>
          <span className="text-green-700 font-medium">Returned</span>
          <span className="text-right text-gray-900">
            {formatUSDT(d.returned_usdt || 0)}
            <span className="text-gray-400 ml-1">
              ({d.returned_count || 0})
            </span>
          </span>
        </div>
      </div>
    );
  };

  // Single slice -> render full circle (still hoverable)
  if (data.length === 1) {
    return (
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle
            cx={cx}
            cy={cy}
            r={radius}
            fill={PIE_COLORS[0]}
            onMouseMove={(e) => handleMove(e, 0)}
            onMouseLeave={() => setHover(null)}
            style={{ cursor: "pointer" }}
          />
        </svg>
        {renderTooltip()}
      </div>
    );
  }

  let cumulative = 0;
  const slices = data.map((d, idx) => {
    const value = d.total_usdt || 0;
    const startAngle = (cumulative / total) * 2 * Math.PI;
    cumulative += value;
    const endAngle = (cumulative / total) * 2 * Math.PI;

    const x1 = cx + radius * Math.sin(startAngle);
    const y1 = cy - radius * Math.cos(startAngle);
    const x2 = cx + radius * Math.sin(endAngle);
    const y2 = cy - radius * Math.cos(endAngle);
    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;

    const path = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    const isHovered = hover?.index === idx;

    return (
      <path
        key={idx}
        d={path}
        fill={PIE_COLORS[idx % PIE_COLORS.length]}
        stroke="#ffffff"
        strokeWidth={isHovered ? 3 : 2}
        style={{
          cursor: "pointer",
          transform: isHovered ? "scale(1.03)" : "scale(1)",
          transformOrigin: `${cx}px ${cy}px`,
          transition: "transform 120ms ease-out",
          filter: isHovered
            ? "drop-shadow(0 2px 4px rgba(0,0,0,0.15))"
            : "none",
        }}
        onMouseMove={(e) => handleMove(e, idx)}
        onMouseLeave={() => setHover(null)}
      />
    );
  });

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {slices}
      </svg>
      {renderTooltip()}
    </div>
  );
};

const InvestorHistoryPage = () => {
  const { id } = useParams();
  const { hasColumnPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [historyData, setHistoryData] = useState(null);
  const [error, setError] = useState(null);

  // Helper function to check if a column should be visible
  const canShowColumn = (columnKey) => {
    return hasColumnPermission(columnKey);
  };

  // Render a mobile number value, falling back to a styled "UNKNOWN" badge
  // when the value is empty/null. Used everywhere mobile numbers are shown.
  const renderMobile = (value) => {
    if (value && String(value).trim() !== "") return value;
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">
        UNKNOWN
      </span>
    );
  };

  // Format a USDT value as a $ amount with 2 decimal places
  const formatUSDT = (value) => {
    const num = parseFloat(value);
    if (Number.isNaN(num)) return "N/A";
    return `$${num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  useEffect(() => {
    fetchInvestorHistory();
  }, [id]);

  const fetchInvestorHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/api/investors/${id}/history`);
      setHistoryData(response.data);
    } catch (err) {
      console.error("Error fetching investor history:", err);
      setError(
        err.response?.data?.message || "Failed to load investor history"
      );
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      const toast = document.createElement("div");
      toast.className =
        "fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 transition-opacity duration-300";
      toast.innerHTML = `
        <div class="flex items-center space-x-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
          <span>Copied to clipboard!</span>
        </div>
      `;
      document.body.appendChild(toast);
      setTimeout(() => {
        toast.style.opacity = "0";
        setTimeout(() => {
          if (document.body.contains(toast)) {
            document.body.removeChild(toast);
          }
        }, 300);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="text-gray-600 text-lg">
            Loading Investor History...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-xl font-bold text-red-800 mb-2">Error</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <Link
            to="/investors"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Investors
          </Link>
        </div>
      </div>
    );
  }

  if (!historyData) {
    return null;
  }

  const {
    investor,
    referrer,
    directReferrals,
    indirectReferrals,
    transactions,
    stats,
    mobileAggregation,
  } = historyData;

  const mobileSchemeData = mobileAggregation?.schemeBreakdown || [];
  const mobileTotalUSDT = mobileAggregation?.totalUSDTByMobile || 0;
  const mobileTotalRecords = mobileAggregation?.totalRecordsByMobile || 0;
  const mobileWithUNSUSDT = mobileAggregation?.withUNSUSDTByMobile || 0;
  const mobileWithUNSRecords = mobileAggregation?.withUNSRecordsByMobile || 0;
  const mobileReturnedUSDT = mobileAggregation?.returnedUSDTByMobile || 0;
  const mobileReturnedRecords = mobileAggregation?.returnedRecordsByMobile || 0;
  const mobileTotalForPercent = mobileSchemeData.reduce(
    (s, d) => s + (d.total_usdt || 0),
    0
  );

  return (
    <div className="p-6">
      {/* Navigation Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/investors"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Investors
            </Link>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <span>•</span>
              <span>Investor History & Referral Chain</span>
            </div>
          </div>
        </div>

        {/* Page Title */}
        <div className="mt-4">
          <div className="flex items-center gap-3 mb-2">
            <ChartBarIcon className="h-8 w-8 text-gray-700" />
            <h1 className="text-3xl font-bold text-gray-900">
              Investor History Dashboard
            </h1>
          </div>
          <p className="text-gray-600">
            Complete history, referral network, and transaction details for{" "}
            {investor.buyer_name}
          </p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white border-2 border-blue-100 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 p-6">
          <div className="flex items-center justify-between">
            <div className="w-10 h-10 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center">
              <CurrencyDollarIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900 mb-1">
                ${stats.totalUSDTReceived.toLocaleString()}
              </p>
              <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                Total USDT Received
              </h3>
            </div>
          </div>
        </div>

        <div className="bg-white border-2 border-green-100 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 p-6">
          <div className="flex items-center justify-between">
            <div className="w-10 h-10 bg-green-50 border border-green-200 rounded-lg flex items-center justify-center">
              <UsersIcon className="w-5 h-5 text-green-600" />
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900 mb-1">
                {stats.totalDirectReferrals}
              </p>
              <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                Direct Referrals
              </h3>
            </div>
          </div>
        </div>

        <div className="bg-white border-2 border-purple-100 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 p-6">
          <div className="flex items-center justify-between">
            <div className="w-10 h-10 bg-purple-50 border border-purple-200 rounded-lg flex items-center justify-center">
              <UserGroupIcon className="w-5 h-5 text-purple-600" />
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900 mb-1">
                {stats.totalIndirectReferrals}
              </p>
              <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                Indirect Referrals
              </h3>
            </div>
          </div>
        </div>

        <div className="bg-white border-2 border-orange-100 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 p-6">
          <div className="flex items-center justify-between">
            <div className="w-10 h-10 bg-orange-50 border border-orange-200 rounded-lg flex items-center justify-center">
              <ArrowTrendingUpIcon className="w-5 h-5 text-orange-600" />
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900 mb-1">
                ${stats.directReferralsUSDT.toLocaleString()}
              </p>
              <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                Referrals USDT
              </h3>
            </div>
          </div>
        </div>
      </div>

      {/* Investor Details Card */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 mb-8">
        <div className="flex items-center mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
            <DocumentTextIcon className="w-5 h-5 text-blue-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Investor Details</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Buyer Name
            </label>
            <p className="text-gray-900 font-semibold">{investor.buyer_name}</p>
          </div>
          {canShowColumn('mobile_number') && (
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Mobile Number
            </label>
            <p className="text-gray-900">{renderMobile(investor.mobile_number)}</p>
          </div>
          )}
          {canShowColumn('wallet_address') && (
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Wallet Address
            </label>
            <div className="flex items-center space-x-2">
              <p
                className="text-gray-900 font-mono text-sm truncate"
                title={investor.wallet_address}
              >
                {investor.wallet_address}
              </p>
              <button
                onClick={() => copyToClipboard(investor.wallet_address)}
                className="text-blue-600 hover:text-blue-800 p-1"
                title="Copy wallet address"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              </button>
            </div>
          </div>
          )}
          {canShowColumn('purchase_date') && (
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Purchase Date
            </label>
            <p className="text-gray-900">{investor.purchase_date}</p>
          </div>
          )}
          {canShowColumn('return_date') && (
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Return Date
            </label>
            <p className="text-gray-900">{investor.return_date || "N/A"}</p>
          </div>
          )}
          {canShowColumn('status') && (
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Status
            </label>
            <span
              className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                investor.status === "With UNS"
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-green-100 text-green-800"
              }`}
            >
              {investor.status}
            </span>
          </div>
          )}
          {canShowColumn('scheme_name') && (
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Scheme
            </label>
            <p className="text-gray-900">{investor.scheme_name}</p>
          </div>
          )}
          {canShowColumn('person_place') && (
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Place
            </label>
            <p className="text-gray-900">{investor.person_place}</p>
          </div>
          )}
          {canShowColumn('usdt_received') && (
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              USDT Received
            </label>
            <p className="text-gray-900 font-semibold">
              {investor.usdt_received || "N/A"}
            </p>
          </div>
          )}
        </div>

        {/* Mobile-based aggregation: Pie chart + Total USDT */}
        {canShowColumn('mobile_number') && mobileAggregation && mobileAggregation.aggregation_basis && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4 gap-2">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Investments Overview
                </h3>
                <p className="text-sm text-gray-500">
                  {mobileAggregation.aggregation_basis === 'mobile' ? (
                    <>
                      Aggregated across all wallet addresses linked to mobile{" "}
                      <span className="font-mono text-gray-700">
                        {mobileAggregation.mobile_number}
                      </span>
                    </>
                  ) : (
                    <>
                      Mobile is{" "}
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold bg-gray-100 text-gray-600 border border-gray-200 align-middle">
                        UNKNOWN
                      </span>
                      {" "}— aggregated by buyer name{" "}
                      <span className="font-semibold text-gray-700">
                        {mobileAggregation.buyer_name || investor.buyer_name}
                      </span>
                    </>
                  )}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg px-4 py-3 min-w-[180px]">
                  <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">
                    Total USDT Invested / Received
                  </p>
                  <p className="text-2xl font-bold text-emerald-900 mt-1">
                    {formatUSDT(mobileTotalUSDT)}
                  </p>
                  <p className="text-[11px] text-emerald-700/80 mt-1">
                    {mobileTotalRecords} record
                    {mobileTotalRecords === 1 ? "" : "s"} total
                  </p>
                </div>
                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg px-4 py-3 min-w-[180px]">
                  <p className="text-xs font-semibold text-yellow-700 uppercase tracking-wide">
                    With UNS (Active)
                  </p>
                  <p className="text-2xl font-bold text-yellow-900 mt-1">
                    {formatUSDT(mobileWithUNSUSDT)}
                  </p>
                  <p className="text-[11px] text-yellow-700/80 mt-1">
                    {mobileWithUNSRecords} record
                    {mobileWithUNSRecords === 1 ? "" : "s"}
                  </p>
                </div>
                <div className="bg-green-50 border-2 border-green-200 rounded-lg px-4 py-3 min-w-[180px]">
                  <p className="text-xs font-semibold text-green-700 uppercase tracking-wide">
                    Returned
                  </p>
                  <p className="text-2xl font-bold text-green-900 mt-1">
                    {formatUSDT(mobileReturnedUSDT)}
                  </p>
                  <p className="text-[11px] text-green-700/80 mt-1">
                    {mobileReturnedRecords} record
                    {mobileReturnedRecords === 1 ? "" : "s"}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              <div className="flex justify-center">
                <SchemePieChart data={mobileSchemeData} />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  Scheme Breakdown
                </h4>
                {mobileSchemeData.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No scheme data available.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {mobileSchemeData.map((item, idx) => {
                      const pct =
                        mobileTotalForPercent > 0
                          ? (item.total_usdt / mobileTotalForPercent) * 100
                          : 0;
                      return (
                        <li
                          key={idx}
                          className="text-sm border-b border-gray-100 pb-2 last:border-b-0"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2 min-w-0">
                              <span
                                className="inline-block w-3 h-3 rounded-sm flex-shrink-0"
                                style={{
                                  backgroundColor:
                                    PIE_COLORS[idx % PIE_COLORS.length],
                                }}
                              />
                              <span
                                className="text-gray-800 truncate"
                                title={item.scheme_name}
                              >
                                {item.scheme_name}
                                {item.scheme_code
                                  ? ` (${item.scheme_code})`
                                  : ""}
                              </span>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <span className="font-semibold text-gray-900">
                                {formatUSDT(item.total_usdt)}
                              </span>
                              <span className="text-gray-500 ml-2 text-xs">
                                ({pct.toFixed(1)}% · {item.investment_count})
                              </span>
                            </div>
                          </div>
                          <div className="ml-5 mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
                            <span className="text-yellow-700">
                              <span className="font-medium">With UNS:</span>{" "}
                              {formatUSDT(item.with_uns_usdt || 0)}
                              <span className="text-gray-400 ml-1">
                                ({item.with_uns_count || 0})
                              </span>
                            </span>
                            <span className="text-green-700">
                              <span className="font-medium">Returned:</span>{" "}
                              {formatUSDT(item.returned_usdt || 0)}
                              <span className="text-gray-400 ml-1">
                                ({item.returned_count || 0})
                              </span>
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Referrer Information */}
      {referrer && (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg shadow-md border-2 border-indigo-200 p-6 mb-8">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-3">
              <CheckCircleIcon className="w-5 h-5 text-indigo-600" />
            </div>
            <h2 className="text-xl font-bold text-indigo-900">Referred By</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-indigo-700 mb-1">
                Name
              </label>
              <Link
                to={`/investors/${referrer.id}/history`}
                className="text-indigo-900 font-semibold hover:text-indigo-600 underline"
              >
                {referrer.buyer_name}
              </Link>
            </div>
            {canShowColumn('mobile_number') && (
            <div>
              <label className="block text-sm font-medium text-indigo-700 mb-1">
                Mobile
              </label>
              <p className="text-indigo-900">{referrer.mobile_number}</p>
            </div>
            )}
            {canShowColumn('wallet_address') && (
            <div>
              <label className="block text-sm font-medium text-indigo-700 mb-1">
                Wallet Address
              </label>
              <div className="flex items-center space-x-2">
                <p
                  className="text-indigo-900 font-mono text-sm truncate"
                  title={referrer.wallet_address}
                >
                  {referrer.wallet_address}
                </p>
                <button
                  onClick={() => copyToClipboard(referrer.wallet_address)}
                  className="text-indigo-600 hover:text-indigo-800 p-1"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </button>
              </div>
            </div>
            )}
            {canShowColumn('status') && (
            <div>
              <label className="block text-sm font-medium text-indigo-700 mb-1">
                Status
              </label>
              <span
                className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                  referrer.status === "With UNS"
                    ? "bg-yellow-100 text-yellow-800"
                    : "bg-green-100 text-green-800"
                }`}
              >
                {referrer.status}
              </span>
            </div>
            )}
            {canShowColumn('scheme_name') && (
            <div>
              <label className="block text-sm font-medium text-indigo-700 mb-1">
                Scheme
              </label>
              <p className="text-indigo-900">{referrer.scheme_name}</p>
            </div>
            )}
            {canShowColumn('usdt_received') && (
            <div>
              <label className="block text-sm font-medium text-indigo-700 mb-1">
                Total USDT
                <span className="ml-2 text-xs font-normal text-indigo-500">
                  (by mobile)
                </span>
              </label>
              <p className="text-indigo-900 font-semibold">
                {formatUSDT(referrer.usdt_received)}
                {referrer.record_count > 1 && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                    {referrer.record_count} records
                  </span>
                )}
              </p>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
                <span className="text-yellow-700">
                  <span className="font-medium">With UNS:</span>{" "}
                  {formatUSDT(referrer.with_uns_usdt || 0)}
                  <span className="text-gray-500 ml-1">
                    ({referrer.with_uns_count || 0})
                  </span>
                </span>
                <span className="text-green-700">
                  <span className="font-medium">Returned:</span>{" "}
                  {formatUSDT(referrer.returned_usdt || 0)}
                  <span className="text-gray-500 ml-1">
                    ({referrer.returned_count || 0})
                  </span>
                </span>
              </div>
            </div>
            )}
          </div>
        </div>
      )}

      {/* Direct Referrals */}
      {directReferrals.length > 0 && (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 mb-8">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
              <UsersIcon className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">
              Direct Referrals ({directReferrals.length})
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Name
                  </th>
                  {canShowColumn('mobile_number') && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Mobile
                  </th>
                  )}
                  {canShowColumn('purchase_date') && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Purchase Date
                  </th>
                  )}
                  {canShowColumn('usdt_received') && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    USDT
                  </th>
                  )}
                  {canShowColumn('status') && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  )}
                  {canShowColumn('actions') && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {directReferrals.map((referral) => (
                  <tr key={referral.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {referral.buyer_name}
                    </td>
                    {canShowColumn('mobile_number') && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {referral.mobile_number}
                    </td>
                    )}
                    {canShowColumn('purchase_date') && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {referral.purchase_date}
                    </td>
                    )}
                    {canShowColumn('usdt_received') && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {referral.usdt_received != null && referral.usdt_received !== ""
                            ? formatUSDT(referral.usdt_received)
                            : "N/A"}
                        </span>
                        {referral.record_count > 1 && (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700"
                            title="Aggregated across multiple wallet addresses linked to the same mobile number"
                          >
                            {referral.record_count}×
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-2 text-[11px]">
                        <span className="text-yellow-700" title="With UNS">
                          UNS: {formatUSDT(referral.with_uns_usdt || 0)}
                          <span className="text-gray-400 ml-0.5">
                            ({referral.with_uns_count || 0})
                          </span>
                        </span>
                        <span className="text-green-700" title="Returned">
                          Ret: {formatUSDT(referral.returned_usdt || 0)}
                          <span className="text-gray-400 ml-0.5">
                            ({referral.returned_count || 0})
                          </span>
                        </span>
                      </div>
                    </td>
                    )}
                    {canShowColumn('status') && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          referral.status === "With UNS"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {referral.status}
                      </span>
                    </td>
                    )}
                    {canShowColumn('actions') && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <Link
                        to={`/investors/${referral.id}/history`}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View History
                      </Link>
                    </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Indirect Referrals */}
      {indirectReferrals.length > 0 && (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 mb-8">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
              <UserGroupIcon className="w-5 h-5 text-purple-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">
              Indirect Referrals (Level 2) - {stats.totalIndirectReferrals}{" "}
              Total
            </h2>
          </div>
          <div className="space-y-6">
            {indirectReferrals.map((group, idx) => (
              <div
                key={idx}
                className="bg-gray-50 rounded-lg p-4 border border-gray-200"
              >
                <h3 className="font-semibold text-gray-900 mb-3">
                  Referred by: {group.referrer.buyer_name} (
                  {group.referrals.length} referrals)
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Name
                        </th>
                        {canShowColumn('mobile_number') && (
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Mobile
                        </th>
                        )}
                        {canShowColumn('purchase_date') && (
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Purchase Date
                        </th>
                        )}
                        {canShowColumn('usdt_received') && (
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          USDT
                        </th>
                        )}
                        {canShowColumn('status') && (
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Status
                        </th>
                        )}
                        {canShowColumn('actions') && (
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Actions
                        </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {group.referrals.map((referral) => (
                        <tr key={referral.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {referral.buyer_name}
                          </td>
                          {canShowColumn('mobile_number') && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {referral.mobile_number}
                          </td>
                          )}
                          {canShowColumn('purchase_date') && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {referral.purchase_date}
                          </td>
                          )}
                          {canShowColumn('usdt_received') && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">
                                {referral.usdt_received != null && referral.usdt_received !== ""
                                  ? formatUSDT(referral.usdt_received)
                                  : "N/A"}
                              </span>
                              {referral.record_count > 1 && (
                                <span
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700"
                                  title="Aggregated across multiple wallet addresses linked to the same mobile number"
                                >
                                  {referral.record_count}×
                                </span>
                              )}
                            </div>
                            <div className="mt-1 flex flex-wrap gap-x-2 text-[11px]">
                              <span className="text-yellow-700" title="With UNS">
                                UNS: {formatUSDT(referral.with_uns_usdt || 0)}
                                <span className="text-gray-400 ml-0.5">
                                  ({referral.with_uns_count || 0})
                                </span>
                              </span>
                              <span className="text-green-700" title="Returned">
                                Ret: {formatUSDT(referral.returned_usdt || 0)}
                                <span className="text-gray-400 ml-0.5">
                                  ({referral.returned_count || 0})
                                </span>
                              </span>
                            </div>
                          </td>
                          )}
                          {canShowColumn('status') && (
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                referral.status === "With UNS"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-green-100 text-green-800"
                              }`}
                            >
                              {referral.status}
                            </span>
                          </td>
                          )}
                          {canShowColumn('actions') && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <Link
                              to={`/investors/${referral.id}/history`}
                              className="text-blue-600 hover:text-blue-800 font-medium"
                            >
                              View History
                            </Link>
                          </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Transaction History */}
      {transactions.length > 0 && (canShowColumn('transaction_hash') || canShowColumn('transaction_date') || canShowColumn('from_wallet') || canShowColumn('to_wallet') || canShowColumn('usdt_received') || canShowColumn('status')) && (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 mb-8">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
              <ClockIcon className="w-5 h-5 text-orange-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">
              Transaction History ({transactions.length})
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {canShowColumn('transaction_date') && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                  )}
                  {canShowColumn('transaction_hash') && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Transaction Hash
                  </th>
                  )}
                  {canShowColumn('from_wallet') && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    From
                  </th>
                  )}
                  {canShowColumn('to_wallet') && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    To
                  </th>
                  )}
                  {canShowColumn('usdt_received') && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    USDT
                  </th>
                  )}
                  {canShowColumn('status') && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map((tx, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    {canShowColumn('transaction_date') && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {tx.date_time}
                    </td>
                    )}
                    {canShowColumn('transaction_hash') && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">
                      <div className="flex items-center space-x-2">
                        <span
                          className="text-gray-900"
                          title={tx.transaction_hash}
                        >
                          {tx.transaction_hash?.substring(0, 10)}...
                        </span>
                        <button
                          onClick={() => copyToClipboard(tx.transaction_hash)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                    )}
                    {canShowColumn('from_wallet') && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      {tx.from_wallet_address?.substring(0, 10)}...
                    </td>
                    )}
                    {canShowColumn('to_wallet') && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      {tx.to_wallet_address?.substring(0, 10)}...
                    </td>
                    )}
                    {canShowColumn('usdt_received') && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {tx.usdt_received}
                    </td>
                    )}
                    {canShowColumn('status') && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          tx.hold_status === "WITH UNS"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {tx.hold_status}
                      </span>
                    </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestorHistoryPage;
